import { getDefaultProgramResponses } from './CreateApplicationSection5';

// Picking a different course clears what was entered for the one just left.
// The warning before the pick and the clearing after it both read
// courseChangeLoss, so they cannot disagree about what is lost.

const isFilled = (value) => value !== '' && value != null && value !== false;

const deepEqual = (a, b) => {
    if (a === b) return true;
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') {
        return false;
    }
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const keys = Object.keys(a);
    return (
        keys.length === Object.keys(b).length &&
        keys.every((key) => deepEqual(a[key], b[key]))
    );
};

const hasSectionDetails = (value) =>
    !!(
        value.crn ||
        value.enrollment ||
        value.sectionNumber ||
        value.modality ||
        (value.meetings || []).some((meeting) =>
            ['type', 'days', 'time', 'room'].some((key) => meeting[key])
        )
    );

// A duplicate keeps its ranked programs and answers, apart from who the LA
// nominee is, which went with the named students. Section details, modality
// and the kinds of meetings belong to the section left, except that a
// duplicate moving to another section of the same course keeps the modality
// and the kinds of meetings. With no course loaded yet
// ("My course isn't listed"), the section details are for the course being
// added, so they stay.
export function courseChangeLoss({
    courseConfirmation,
    namedStudents,
    selectedPrograms,
    programResponses,
    isDuplicate,
    hasCourse,
}) {
    return {
        sectionDetails: hasCourse && hasSectionDetails(courseConfirmation),
        namedStudents: namedStudents.length > 0,
        programs: !isDuplicate && selectedPrograms.length > 0,
        programAnswers:
            !isDuplicate &&
            !deepEqual(programResponses, getDefaultProgramResponses()),
        nominee:
            isDuplicate &&
            (isFilled(programResponses.la?.nomineeStatus) ||
                isFilled(programResponses.la?.nomineeDeclinePreference)),
    };
}

export const losesAnything = (loss) => Object.values(loss).some(Boolean);

const joinList = (items) => {
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]}, and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

export function courseChangeWarning(loss, isDuplicate = false) {
    const items = [];
    if (loss.sectionDetails) {
        items.push(
            'the CRN, enrollment, section, modality and meeting times you entered below'
        );
    }
    if (loss.namedStudents) items.push('the students you named');
    if (loss.programs && loss.programAnswers) {
        items.push('the programs you ranked and your answers about them');
    } else if (loss.programs) {
        items.push('the programs you ranked');
    } else if (loss.programAnswers) {
        items.push('your answers about the programs');
    }
    if (loss.nominee) items.push('your answers about naming an LA');
    const kept =
        isDuplicate && loss.sectionDetails
            ? ' Another section of this same course keeps the modality and the kinds of meetings.'
            : '';
    return `Picking a different course clears ${joinList(items)}.${kept}`;
}
